/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {RxStomp} from '@stomp/rx-stomp';
import {EMPTY, merge, Observable, ObservableInput, of, Subscriber, Subscription} from 'rxjs';
import {mapTo} from 'rxjs/operators';
import {
	stompClientConnectionEstablished,
	stompClientSubscribedToTopic,
	stompClientUnsubscribedFromTopic,
	stompClientWebSocketErrorOccurred,
	stompMessageReceived
} from './actions';
import {AnyAction} from 'redux';

export const CLIENT_IDENTIFIER_IAM = 'IAM';

class StompClient {

	private rxStomp: RxStomp;

	private readonly clientIdentifier: string;

	private readonly wssQueryEventsBaseUrl: string;

	private readonly subscribedTopics: {
		[key: string]: Subscription;
	};

	constructor(clientIdentifier: string, baseUrl: string) {
		this.clientIdentifier = clientIdentifier;
		this.wssQueryEventsBaseUrl =
			baseUrl.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/wss/query-events';

		this.rxStomp = new RxStomp();
		this.rxStomp.configure({
			brokerURL: this.wssQueryEventsBaseUrl,
			reconnectDelay: 1000
		});

		this.subscribedTopics = {};
	}

	connect(): Observable<AnyAction> {
		if (!this.rxStomp.connected())
			this.rxStomp.activate();

		const connections: Observable<AnyAction> = this.rxStomp.connected$
			.pipe(mapTo(stompClientConnectionEstablished({clientIdentifier: this.clientIdentifier})));
		const errors: ObservableInput<AnyAction> = this.rxStomp.webSocketErrors$
			.pipe(mapTo(stompClientWebSocketErrorOccurred({clientIdentifier: this.clientIdentifier})));
		return merge(connections, errors);
	}

	subscribe(topic: string): Observable<AnyAction> {
		if (this.subscribedTopics[topic])
			return EMPTY;

		return new Observable<AnyAction>((observer: Subscriber<AnyAction>) => {
			this.subscribedTopics[topic] = this.rxStomp.watch(topic).subscribe(
				(frame) => {
					observer.next(stompMessageReceived({topic, eventType: frame.headers.eventType, eventBody: JSON.parse(frame.body)}));
				}
			);
			observer.next(stompClientSubscribedToTopic({topic}));
		});
	}

	unsubscribe(topic: string): Observable<AnyAction> {
		if (!this.subscribedTopics[topic])
			return EMPTY;

		this.subscribedTopics[topic].unsubscribe();
		delete this.subscribedTopics[topic];

		return of(stompClientUnsubscribedFromTopic({topic}));
	}
}

export default StompClient;
